/**
 * Attachments Section Component
 * 
 * File upload and management section for profiles.
 * Per design.md: drag-drop UI, semantic tokens, lucide icons.
 * Per workspace rules: No emojis, use @uprosal/ui components.
 */

import { useState, useRef, useCallback } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc, Id } from 'convex/_generated/dataModel';
import { motion } from 'framer-motion';
import {
    Button,
    Skeleton,
    Loader2,
    Upload,
    Paperclip,
    AlertCircle,
} from '@uprosal/ui';
import { AttachmentCard } from './AttachmentCard';

interface AttachmentsSectionProps {
    profileId: Id<'connectedProfiles'>;
    profileLabel: string;
}

// Allowed file extensions for display
const ALLOWED_EXTENSIONS = [
    '.png', '.jpg', '.jpeg', '.gif', '.webp',
    '.pdf', '.doc', '.docx',
    '.ppt', '.pptx',
    '.xls', '.xlsx',
    '.zip',
];

// Max file size: 25 MB
const MAX_FILE_SIZE = 25 * 1024 * 1024;

// Max files per profile
const MAX_FILES_PER_PROFILE = 20;

export function AttachmentsSection({ profileId }: AttachmentsSectionProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Fetch attachments for this profile
    type Attachment = Doc<'profileAttachments'> & { url: string | null };
    const attachments = useQuery(api.queries.attachments.listByProfile, { profileId }) as Attachment[] | undefined;

    // Mutations
    const generateUploadUrl = useMutation(api.mutations.attachments.generateUploadUrl);
    const createAttachment = useMutation(api.mutations.attachments.create);

    const canUploadMore = (attachments?.length ?? 0) < MAX_FILES_PER_PROFILE;

    // Validate file before upload
    const validateFile = (file: File): string | null => {
        // Check size
        if (file.size > MAX_FILE_SIZE) {
            return `File "${file.name}" is too large. Maximum size is 25 MB.`;
        }

        // Check extension
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
            return `File type "${extension}" is not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
        }

        return null;
    };

    // Handle file upload
    const handleUpload = useCallback(async (files: FileList | File[]) => {
        if (!canUploadMore) {
            setError(`Maximum ${MAX_FILES_PER_PROFILE} files per profile. Delete some files first.`);
            return;
        }

        const fileArray = Array.from(files);
        if (fileArray.length === 0) return;

        // Validate all files first
        for (const file of fileArray) {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        // Check if total count would exceed limit
        const currentCount = attachments?.length ?? 0;
        if (currentCount + fileArray.length > MAX_FILES_PER_PROFILE) {
            setError(`Cannot upload ${fileArray.length} files. You can only add ${MAX_FILES_PER_PROFILE - currentCount} more files.`);
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            for (let i = 0; i < fileArray.length; i++) {
                const file = fileArray[i];
                setUploadProgress(`Uploading ${i + 1}/${fileArray.length}: ${file.name}`);

                // Step 1: Get upload URL
                const uploadUrl = await generateUploadUrl();

                // Step 2: Upload file to the URL
                const result = await fetch(uploadUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': file.type },
                    body: file,
                });

                if (!result.ok) {
                    throw new Error(`Failed to upload ${file.name}`);
                }

                const { storageId } = await result.json();

                // Step 3: Create attachment record
                await createAttachment({
                    profileId,
                    storageId,
                    filename: file.name,
                    mimeType: file.type || 'application/octet-stream',
                    size: file.size,
                });
            }

            setUploadProgress(null);
        } catch (err) {
            console.error('Upload failed:', err);
            setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    }, [profileId, attachments?.length, canUploadMore, generateUploadUrl, createAttachment]);

    // Handle click to upload
    const handleClick = () => {
        fileInputRef.current?.click();
    };

    // Handle file input change
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleUpload(e.target.files);
            // Reset input so same file can be uploaded again
            e.target.value = '';
        }
    };

    // Drag and drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleUpload(e.dataTransfer.files);
        }
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <Paperclip className="w-5 h-5" />
                        Attachments
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        Upload work samples for quick attachment on Upwork proposals
                    </p>
                </div>
                <span className="text-xs text-muted-foreground">
                    {attachments?.length ?? 0} / {MAX_FILES_PER_PROFILE} files
                </span>
            </div>

            {/* Upload Zone */}
            <div
                className={`
                    relative border-2 border-dashed rounded-lg p-8 text-center
                    transition-colors cursor-pointer
                    ${isDragging
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 hover:bg-muted/30'
                    }
                    ${isUploading ? 'pointer-events-none opacity-60' : ''}
                `}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ALLOWED_EXTENSIONS.join(',')}
                    onChange={handleFileChange}
                    className="hidden"
                />

                {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        <p className="text-sm font-medium text-foreground">
                            {uploadProgress || 'Uploading...'}
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                            <Upload className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <p className="text-sm font-medium text-foreground">
                            Drag & drop files here, or click to browse
                        </p>
                        <p className="text-xs text-muted-foreground">
                            PNG, JPG, PDF, DOC, PPT, XLS, ZIP up to 25 MB each
                        </p>
                    </div>
                )}
            </div>

            {/* Error message */}
            {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto h-6 px-2 text-xs"
                        onClick={() => setError(null)}
                    >
                        Dismiss
                    </Button>
                </div>
            )}

            {/* Loading state */}
            {attachments === undefined && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="aspect-[4/3] rounded-lg" />
                    ))}
                </div>
            )}

            {/* Empty state */}
            {attachments?.length === 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-8 text-muted-foreground"
                >
                    <p>No attachments yet</p>
                    <p className="text-sm mt-1">
                        Upload your portfolio pieces, screenshots, or documents
                    </p>
                </motion.div>
            )}

            {/* Attachment grid */}
            {attachments && attachments.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {attachments.map((attachment, index) => (
                        <motion.div
                            key={attachment._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                        >
                            <AttachmentCard attachment={attachment} />
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}
